import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { MetricInferenceSourceDataLayerTagComponent } from '~/_shared/components/metricsList/item/metricInferenceSourceDataLayerTag.component';
import { MetricInferenceSourceFillConfigurationTagComponent } from '~/_shared/components/metricsList/item/metricInferenceSourceFillConfigurationTag.component';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import type {
  FillConfigMetricInferenceSource, VirtualMetricInferenceSourceInfo,
} from '~/_shared/utils/metric/metrics.types';
import { notUndefined } from '~/_shared/utils/typeGuards';
import { boundaryTerritoryGroupActivate } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import { useBTGsGroupedByBoundaryGroupSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { useIsMobileScreenSelector } from '~/store/frontendState/deviceInfo/deviceInfo.selector';
import { openMapTool } from '~/store/frontendState/leftSidebar/leftSidebar.actionCreators';
import { boundaryTerritoryGroupHighlightStarted } from '~/store/frontendState/mapTools/boundary/boundarySidebarState/boundarySidebarState.actionCreators';
import { usePublicMapSettingsSelector } from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.selectors';
import { useIsMapPresentationalSelector } from '~/store/selectors/useMapInfoSelectors';

type MetricInferenceSourceTagContainerProps = {
  inferenceSource?: VirtualMetricInferenceSourceInfo;
};

export const MetricInferenceSourceTagContainer = ({
  inferenceSource,
  ...restProps
}: MetricInferenceSourceTagContainerProps) => {
  const dispatch = useDispatch();
  const groupedBTGs = useBTGsGroupedByBoundaryGroupSelector();
  const isMapPresentational = useIsMapPresentationalSelector();
  const publicMapSettings = usePublicMapSettingsSelector();
  const isMobileScreen = useIsMobileScreenSelector();
  const firstBoundaryGroupId = inferenceSource?.type === 'FillConfiguration' ? inferenceSource.inferenceSources[0]?.btg.boundaryGroupId : undefined;
  const BTGs = notUndefined(firstBoundaryGroupId) ?
    groupedBTGs.all.find((btg) => btg.boundaryGroupId === firstBoundaryGroupId) :
    undefined;
  const mapHasMoreThanOneFill = notUndefined(BTGs) ? (BTGs.boundaryTerritoryGroups.length > 1) : false;

  const activateFill = useCallback((source: FillConfigMetricInferenceSource) => {
    dispatch(boundaryTerritoryGroupActivate(source.btg));
    dispatch(boundaryTerritoryGroupHighlightStarted(source.btg.boundaryGroupId));
  }, [dispatch]);

  const openDataLayersTool = useCallback(() => {
    dispatch(openMapTool(MapTool.DataLayers));
  }, [dispatch]);

  if (isMobileScreen) {
    // mobileMapToolsAsMenuItems is hardcoded not to offer any of the Boundary tools asn as such it should not be available on mobile.
    return null;
  }

  if (isMapPresentational && !publicMapSettings.boundaryTool) {
    return null;
  }

  if (inferenceSource?.type === 'DataLayers') {
    return (
      <MetricInferenceSourceDataLayerTagComponent
        onDataLayerClick={openDataLayersTool}
      />
    );
  }

  if (inferenceSource?.type === 'FillConfiguration') {
    return (
      <MetricInferenceSourceFillConfigurationTagComponent
        {...restProps}
        inferenceSource={inferenceSource}
        onFillClick={activateFill}
        showFillNumbers={mapHasMoreThanOneFill}
      />
    );
  }

  return null;
};
